import '../SectionCard.scss';

import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import Button from '@/src/components/common/Elements/Button/Button';
import { cn } from '@/src/util/ui';

import Icon, { ICONS } from '../../../Icon/Icon';
import Card from '../../Card';

type ScrollableSectonCardProps = {
  children: ReactNode;
  sectionKey?: string;
  className?: string;
  scrollClasses?: string;
  scrollOffset?: number;
  isReportsEnabled?: boolean;
  scrollDirection?: 'scrollY' | 'scrollX' | 'scrollXY';
};

const STATIC_OFFSET = 10;

const getSliderStatus = (slider) => {
  if (!slider) {
    return { hasPrev: false, hasNext: false };
  }

  const { scrollLeft, scrollWidth, clientWidth } = slider;
  const scrollMax = scrollWidth - clientWidth;
  const percentage = (scrollLeft / scrollMax) * 100;

  const hasPrev = percentage > 0;
  const hasNext = percentage < 98;

  return {
    hasPrev,
    hasNext: scrollMax - STATIC_OFFSET - scrollLeft < 1 ? false : hasNext,
  };
};
const ScrollableSectonCard = ({
  className,
  scrollClasses,
  children,
  sectionKey,
  scrollDirection,
  scrollOffset = null,
  isReportsEnabled,
  ...otherProps
}: ScrollableSectonCardProps) => {
  const sliderContainerRef = useRef(null);
  const slider = sliderContainerRef.current;
  const hasArrows = scrollOffset !== null && !isReportsEnabled;

  const containerClasses = cn('card--container_section tw-relative tw-rounded-[25px]', className, {
    'tw-py-[20px] tw-px-[33px]': hasArrows,
    'tw-py-[20px] tw-px-[20px]': !hasArrows,
  });

  const scrollDirectionClasses = cn('card--section_scroll_wrapper', scrollClasses, {
    'tw-overflow-y-scroll tw-overflow-x-hidden': scrollDirection === 'scrollY',
    'tw-overflow-x-scroll tw-overflow-y-hidden': scrollDirection === 'scrollX',
    'tw-overflow-scroll': scrollDirection === 'scrollXY',
  });

  const arrowClasses = cn(
    `tw-absolute tw-top-[50%] tw-translate-y-[-50%] 
    tw-leading-none 
    tw-px-[4px] tw-py-[15px]
    hover:tw-bg-cfo-blue-600 hover:tw-text-white`
  );

  const [sliderStatus, setSliderStatus] = useState({
    hasPrev: false,
    hasNext: false,
  });
  const { hasPrev, hasNext } = sliderStatus;

  const handleSlideLeft = useCallback(() => {
    slider.scrollBy({
      top: 0,
      left: -scrollOffset,
      behavior: 'smooth',
    });
    setSliderStatus(getSliderStatus(slider));
  }, [slider, scrollOffset]);

  const handleSlideRight = useCallback(() => {
    slider.scrollBy({
      top: 0,
      left: scrollOffset,
      behavior: 'smooth',
    });
    setSliderStatus(getSliderStatus(slider));
  }, [slider, scrollOffset]);

  const handleScroll = useCallback(() => {
    setSliderStatus(getSliderStatus(slider));
  }, [slider]);

  // Ensure the dependency to the ref is detected by using sliderContainerRef.current
  useEffect(() => {
    if (!sliderContainerRef.current) {
      return;
    }

    setSliderStatus(getSliderStatus(sliderContainerRef.current));
  }, [sliderContainerRef, sectionKey]);

  return (
    <Card className={containerClasses} {...otherProps}>
      <div
        className={scrollDirectionClasses}
        style={{ minHeight: '120px' }}
        ref={sliderContainerRef}
        onScroll={handleScroll}
      >
        {children}
      </div>

      {hasArrows && (
        <>
          <Button
            className={cn(
              'arrow left-arrow tw-left-0 tw-mr-0 tw-rounded-r-[5px] ',
              arrowClasses,
              'tw-cursor-pointer',
              !hasPrev && 'tw-bg-cfo-grey-500 tw-text-white tw-pointer-events-none',
              hasPrev && 'tw-bg-[#F0F4FF] tw-text-cfo-blue-600'
            )}
            onClick={handleSlideLeft}
          >
            <Icon icon={ICONS.arrow_left} />
          </Button>
          <Button
            className={cn(
              'arrow right-arrow tw-right-0 tw-mr-0 tw-rounded-l-[5px]',
              arrowClasses,
              'tw-cursor-pointer',
              !hasNext && 'tw-bg-cfo-grey-500 tw-text-white tw-pointer-events-none',
              hasNext && 'tw-bg-[#F0F4FF] tw-text-cfo-blue-600'
            )}
            onClick={handleSlideRight}
          >
            <Icon icon={ICONS.arrow_right} />
          </Button>
        </>
      )}
    </Card>
  );
};

export default ScrollableSectonCard;
